import { User, Bot, Globe, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';
import { Visualization } from '../visualization';
import { ThinkingSection } from './ThinkingSection';
import type { ChatMessage, ContentBlock } from '../../types';

interface MessageProps {
  message: ChatMessage;
}

function CitationsList({
  citations,
}: {
  citations: { url: string; title: string; text: string }[];
}) {
  if (!citations || citations.length === 0) return null;

  return (
    <div className="mt-6 rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold text-foreground">
          Sources ({citations.length})
        </h4>
      </div>
      <div className="space-y-2">
        {citations.map((citation, index) => (
          <a
            key={index}
            href={citation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-3 p-3 rounded-md hover:bg-muted/50 transition-colors group"
          >
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate">
                {citation.title || citation.url}
              </div>
              <div className="text-xs text-muted-foreground truncate mt-0.5">
                {new URL(citation.url).hostname}
              </div>
            </div>
            <Globe className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}

function MarkdownContent({ content, isUser }: { content: string; isUser: boolean }) {
  const enhancedComponents = {
    h3: ({ children }: { children?: React.ReactNode }) => (
      <h3 className="mt-6 mb-3 text-lg font-semibold text-foreground">
        {children}
      </h3>
    ),
    h4: ({ children }: { children?: React.ReactNode }) => (
      <h4 className="mt-4 mb-2 text-base font-semibold text-foreground flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
        {children}
      </h4>
    ),
    p: ({ children }: { children?: React.ReactNode }) => (
      <p
        className={cn(
          isUser ? 'mb-1 leading-normal mt-0 last:mb-0' : 'mb-4 leading-relaxed last:mb-0',
          'text-foreground/90'
        )}
      >
        {children}
      </p>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul className="my-4 ml-6 space-y-2">{children}</ul>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <li className="leading-relaxed">{children}</li>
    ),
  };

  return (
    <div
      className={cn(
        'prose prose-sm max-w-none dark:prose-invert [&>*:last-child]:mb-0 [&>*:first-child]:mt-0',
        isUser && '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0'
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          ...enhancedComponents,
          pre: ({ children }) => (
            <pre className="overflow-x-auto rounded-lg bg-nova-dashboard-900 border border-border p-4 text-sm">
              {children}
            </pre>
          ),
          code: ({ className, children, ...props }) => {
            const isInline = !className;
            return isInline ? (
              <code
                className="rounded bg-primary/10 px-1.5 py-0.5 text-sm font-medium text-primary"
                {...props}
              >
                {children}
              </code>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline decoration-primary/30 underline-offset-2"
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="my-4 overflow-x-auto rounded-lg border border-border bg-nova-dashboard-900">
              <table className="w-full text-sm">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border-b border-border bg-muted/50 px-4 py-3 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border-b border-border px-4 py-3">{children}</td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// Clean orphaned markdown markers from text content.
// When a visualization splits a text block mid-markdown (e.g. "**[CHART] AGE DISTRIBUTION**"
// becomes text:"**", viz:chart, text:"AGE DISTRIBUTION**"), the bold markers get orphaned.
// Strip unmatched ** so they don't render as literal asterisks.
function cleanOrphanedMarkdown(content: string): string {
  let text = content;
  const boldMarkers = text.match(/\*\*/g);
  if (boldMarkers && boldMarkers.length % 2 !== 0) {
    // Odd number of ** means unmatched markers — strip from edges
    text = text.replace(/^\s*\*\*\s*/, '');
    text = text.replace(/\s*\*\*\s*$/, '');
  }
  return text.trim();
}

function ContentBlockRenderer({ block, isUser }: { block: ContentBlock; isUser: boolean }) {
  if (block.type === 'text') {
    const cleaned = cleanOrphanedMarkdown(block.content);
    if (!cleaned) return null;
    return <MarkdownContent content={cleaned} isUser={isUser} />;
  }

  if (block.type === 'visualization') {
    return (
      <div className="my-6 rounded-xl border border-border bg-nova-dashboard-900 p-5">
        <Visualization data={block.data} />
      </div>
    );
  }

  return null;
}

export function Message({ message }: MessageProps) {
  const isUser = message.role === 'user';

  return (
    <div
      className={cn(
        'flex gap-4 px-6 items-start',
        isUser ? 'user-message bg-transparent py-2 rounded-lg mx-4 mt-4' : 'bg-transparent py-2 mx-4'
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full shadow-sm',
          isUser
            ? 'bg-gradient-to-br from-primary to-primary/80 mt-0.5'
            : 'bg-gradient-to-br from-secondary to-secondary/80'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-primary-foreground" />
        ) : (
          <Bot className="h-4 w-4 text-secondary-foreground" />
        )}
      </div>
      <div className={cn('flex-1 overflow-hidden', !isUser && 'space-y-2')}>
        {!isUser && (
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Nebula</p>
          </div>
        )}

        {message.thinkingContent && (
          <ThinkingSection content={message.thinkingContent} />
        )}

        {!isUser && message.contentBlocks && message.contentBlocks.length > 0 ? (
          <div className="space-y-0">
            {message.contentBlocks.map((block, index) => (
              <ContentBlockRenderer key={index} block={block} isUser={isUser} />
            ))}
          </div>
        ) : (
          message.content && (
            <MarkdownContent content={message.content} isUser={isUser} />
          )
        )}

        {message.citations && <CitationsList citations={message.citations} />}
      </div>
    </div>
  );
}
